/** @format */

import useAxios from '@/hooks/useAxios.hook';
import { endPoint } from '@/utils';
import { useEffect, useRef, useState } from 'react';

const useCategoryList = (
  categoryCode: string,
  initialData?: any[],
  initialEventCategoryName?: string,
) => {
  // When the page server-renders the first batch, we seed state with it and
  // skip the initial client fetch — the events are already in the HTML.
  const hasInitialData = (initialData?.length ?? 0) > 0;
  const DEFAULT_LIMIT = 10;
  const [initialState, setInitialState] = useState({
    limit: DEFAULT_LIMIT,
    // If the server already rendered page 1, the next fetch must start
    // at page 2 — otherwise the first loadMore() re-requests skip 0 and
    // duplicates the events already on screen.
    skip: hasInitialData ? DEFAULT_LIMIT : 0,
    refreshing: false,
    data: hasInitialData ? (initialData as any[]) : [],
    hasMore: true,
    eventCategoryName: initialEventCategoryName || 'All Events',
  });
  const { skip, refreshing, data, hasMore, eventCategoryName } = initialState;
  const { fetchData, loading } = useAxios(!hasInitialData);
  // Scroll/resize can fire loadMore several times before the previous
  // request's state update commits; this ref blocks re-entrant calls
  // synchronously, which `refreshing` state (async) can't do.
  const isFetchingMoreRef = useRef(false);
  // InfiniteScroll can still invoke a stale loadMore closure (from before
  // its listener was swapped in) after a real scroll event. Reading skip
  // via this ref instead of the closed-over `skip` above ensures even a
  // stale call fetches the current next page rather than repeating one.
  const stateRef = useRef(initialState);
  stateRef.current = initialState;
  const updateState = (newState: any) => {
    setInitialState((prevState) => ({
      ...prevState,
      ...newState,
    }));
  };

  const fetchSearchData = async ({
    limit = 15,
    skip,
  }: {
    limit?: number;
    skip: number;
  }) => {
    isFetchingMoreRef.current = true;
    try {
      const res = await fetchData({
        url: endPoint.FETCH_EVENTS_BY_CATEGORY_BY_PAGE_GUEST,
        params: {
          limit,
          skip,
        },
        method: 'POST',
        data: { categoryCode },
        loading,
      });
      updateState({
        data: res?.data?.events || [],
        skip: limit,
        hasMore:
          res?.data?.events.length > limit ||
          res?.data?.events.length === limit,
        eventCategoryName: res?.data?.eventCategoryName || eventCategoryName,
      });
    } finally {
      isFetchingMoreRef.current = false;
    }
  };
  const loadMore = async () => {
    const current = stateRef.current;
    if (current.hasMore && current.data?.length && !isFetchingMoreRef.current) {
      isFetchingMoreRef.current = true;
      updateState({
        refreshing: true,
      });
      try {
        const res = await fetchData({
          url: endPoint.FETCH_EVENTS_BY_CATEGORY_BY_PAGE_GUEST,
          params: {
            limit: current.limit,
            skip: current.skip,
          },
          method: 'POST',
          data: { categoryCode },
          loading: false,
        });
        const newEvents = res?.data?.events || [];
        setInitialState((prev) => ({
          ...prev,
          refreshing: false,
          skip: prev.skip + prev.limit,
          hasMore: newEvents.length >= prev.limit,
          data: [...prev.data, ...newEvents],
        }));
      } finally {
        isFetchingMoreRef.current = false;
      }
    }
  };
  useEffect(() => {
    if (!hasInitialData) {
      fetchSearchData({ limit: 10, skip: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryCode]);

  return {
    loading,
    data,
    refreshing,
    hasMore,
    loadMore,
    skip,
    eventCategoryName,
  };
};

export default useCategoryList;
